import humps from 'humps';
import { getCartTotal, getCartCurrencySymbol } from 'cart/utils';
import { devLog, getCustomData, getFeaturedImage } from 'core/utils';

const klaviyoReady = () => {
	if (typeof window !== 'object') {
		devLog("No window variable, skipping Klaviyo");
		return false;
	}
	window._learnq = window._learnq || [];
	return true;
};

export const getProductProperties = () => {
	const listing = getCustomData(window?.cj?.listing, {});
	const featuredImage = getFeaturedImage(listing);
	const imageUrl = `https:${featuredImage.cdnUrl}` || "";
	let termUnit = (listing.termPrices[0]?.cardFrequency || "").replace(".", "");

	const props = {
		"ProductName": listing.name,
		"ProductID": listing.productId,
		"SKU": listing.slug,
		"Categories": listing.categories.map(c => c.shortName),
		"ImageURL": imageUrl,
		"URL": `https://www.cratejoy.com/subscription-box/${listing.slug}/`,
		"Brand": listing.storeName,
		"CurrencySymbol": listing.currencySymbol,
		"TermUnit": termUnit,
		"Price": (listing.discountedPrice || listing.minUnitTermPrice) / 100,
	};

	if (listing.discountedPrice < listing.minUnitTermPrice) {
		props["CompareAtPrice"] = listing.minUnitTermPrice / 100;
	}

	return props;
};

/**
 * @param productInstanceId - for product added to cart
 * @param cart - cart object
 * @param listing - listing object
 */
export const getCartProperties = (productInstanceId, cart, listing = {}) => {
	const cartProducts = cart.products || [];
	const addedItem = cartProducts.find(cp => cp.productInstanceId === productInstanceId) || {};
	let itemSlug = addedItem.listing?.slug ? addedItem.listing.slug : cart.listing.slug;
	let itemImageUrl = addedItem.listing?.image ? addedItem.listing.image : (addedItem.productInstance?.product?.image ? addedItem.productInstance.product.image : cart.listing.image);
	// Enhance... with listing data
	if (listing.id) {
		listing = humps.camelizeKeys(listing);
		const featuredImage = getFeaturedImage(listing);
		if (featuredImage?.cdnUrl) {
			itemImageUrl = featuredImage.cdnUrl;
		}
	}
	const addedCats = listing.categories?.map(c => c.shortName) || [];

	return {
		"$value": getCartTotal([cart]) / 100,
		"AddedItemProductName": addedItem?.productInstance?.product?.name,
		"AddedItemProductID": addedItem?.productInstance?.product?.id,
		"AddedItemSKU": productInstanceId,
		"AddedItemCategories": addedCats,
		"AddedItemImageURL": `https:${itemImageUrl}`,
		"AddedItemURL": `https://www.cratejoy.com/subscription-box/${itemSlug}/`,
		"AddedItemPrice": addedItem.price / 100,
		"AddedItemQuantity": addedItem.quantity,
		"ItemNames": cartProducts.map(cp => cp.productInstance?.product?.name) || [],
		"CheckoutURL": `https://www.cratejoy.com/cart/${cart.id}/`,
		"CurrencySymbol": getCartCurrencySymbol([cart]),
		"Items": cartProducts.map(cp => getCartItemProperties(cp, cart.listing, cart.id)),
	};
};

/**
 * Serialize cart product into Klaviyo `Items`
 * Used by `Added to Cart` & `Started Checkout` events
 *
 * @param {Object} cp - CartProduct
 * @return {Object} Item
 */
const getCartItemProperties = (cp, fallbackListing = {}, cartId = null) => {
	let productSlug = cp.listing?.slug ? cp.listing.slug : fallbackListing.slug;
	let productUrl = 'https://www.cratejoy.com'; // base
	let productImageUrl = cp.listing?.image ? cp.listing.image : (cp.productInstance?.product?.image ? cp.productInstance.product.image : fallbackListing.image);
	let itemPrice = cp.subscriptionTypeTerm?.numCycles ? Math.round(cp.price / cp.subscriptionTypeTerm.numCycles) / 100 : (cp.price / 100);
	let termName = cp.subscriptionTypeTerm?.name || "";
	let termUnit = '';
	if (cp.subscriptionTypeTerm?.numCycles === 1) {
		// hide for term-to-term. implied via "$9/mo"
		termName = '';
	}
	if (cp.productInstance?.product?.billing?.id) {
		let { rebillMonths, rebillWeeks } = cp.productInstance.product.billing;
		let rebillInterval = rebillMonths || rebillWeeks;
		if (rebillMonths === 1 || !rebillInterval) {
			termUnit = 'mo';
		} else if (rebillWeeks === 1) {
			termUnit = 'wk';
		} else {
			termUnit = 'box';
		}
	}
	if (productSlug) {
		productUrl += `/subscription-box/${productSlug}/`;
	} else if (cartId) {
		productUrl += `/cart/${cartId}/`;
	} else {
		productUrl += `/`;
	}
	return {
		"ProductID": cp.productInstance?.product?.id,
		"SKU": cp.subscriptionTypeTermId ? `${cp.productInstanceId}-${cp.subscriptionTypeTermId}` : `${cp.productInstanceId}`,
		"ProductName": cp.productInstance?.product?.name,
		"TermName": termName,
		"TermUnit": termUnit,
		"Quantity": cp.quantity,
		"ItemPrice": itemPrice,
		"RowTotal": (cp.price / 100),
		"ProductURL": productUrl,
		"ImageURL": `https:${productImageUrl}`,
		"ProductCategories": [], // TODO: Yeah...the categories thing again
	};
}

export const getCheckoutProperties = (cart) => {
	// For "Started Checkout"
	// https://developers.klaviyo.com/en/v1-2/docs/guide-to-integrating-a-platform-without-a-pre-built-klaviyo-integration#started-checkout
	const ts = Math.floor(Date.now() / 1000);
	const cartProducts = cart.products || [];
	return {
		"$event_id": `${cart.id}_${ts}`,
		"$value": getCartTotal([cart]) / 100,
		"ItemNames": cartProducts.map(cp => cp.productInstance?.product?.name) || [],
		"CheckoutURL": `https://www.cratejoy.com/checkout?cart_id=${cart.id}`,
		"CurrencySymbol": getCartCurrencySymbol([cart]),
		// TODO: Grr we don't have the categories on the cart but maybe we should
		"Categories": [],
		"Items": cartProducts.map(cp => getCartItemProperties(cp, cart.listing, cart.id)),
	};
};

/**
 * Shortcut for "Viewed Product" & "trackViewedItem" events
 * https://developers.klaviyo.com/en/v1-2/docs/guide-to-integrating-a-subscription-ecommerce-platform#viewed-product-tracking-snippet
 */
export const viewedProduct = () => {
	const properties = getProductProperties();
	track("Viewed Product", properties);

	// Added to a “Recently Viewed Items” list for a profile
	track("trackViewedItem", {
		"Title": properties.ProductName,
		"ItemId": properties.ProductID,
		"Categories": properties.Categories,
		"ImageUrl": properties.ImageURL,
		"Url": properties.URL,
		"Metadata": {
			"Brand": properties.Brand,
			"CurrencySymbol": properties.CurrencySymbol,
			"Price": properties.Price,
			"TermUnit": properties.TermUnit,
			"CompareAtPrice": properties.CompareAtPrice || properties.Price,
		},
	});
};

/**
 * set properties on an individual
 * `email` is required
 * https://developers.klaviyo.com/en/v1-2/docs/javascript-api#identify-people
 * https://developers.klaviyo.com/en/v1-2/docs/identify-api-reference#specific-properties
 * @param {Object} user_properties
 */
export const identify = ({ email, first_name="", last_name="", name="" }) => {
	if (!klaviyoReady() || !email) {
		return;
	}

	const [defaultFirst, ...nameBits] = name.split(" ");
	const defaultLast = nameBits.join(" ");

	const props = { '$email': email };
	if (first_name || defaultFirst) {
		props['$first_name'] = first_name || defaultFirst;
	}
	if (last_name || defaultLast) {
		props['$last_name'] = last_name || defaultLast;
	}

	// TODO: Include `$consent`

	devLog(`Klaviyo(identify)`, props);
	//Identify a user
	window._learnq.push(['identify', props]);
};

/**
 * https://developers.klaviyo.com/en/v1-2/docs/custom-event-tracking
 *
 * @param {String} name
 * @param {Object} properties
 */
export const track = (name, properties) => {
	if (!klaviyoReady()) {
		return;
	}

	devLog(`Klaviyo(track) ${name}`, properties);
	window._learnq.push(["track", name, properties]);
};

const Klaviyo = {
	track,
	identify,
	getCartProperties,
	getCheckoutProperties,
	getProductProperties,
	viewedProduct,
};

export default Klaviyo;
